import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import {
    FormulaBuilderLookup,
    AttributeValue,
    SyntaxCheck,
    SyntaxCheckResponse,
    Sequence,
    ActionAttribute,
    FormulaBuilderType,
    FormulaBuilderExpression,
    SyntaxVisualizeResponse,
    FormulaBuilderConfig
} from './cpq-formula-builder.model';
import { UrlPrefix } from "../common/constants/url-prefix.constants";
import { ListBoxItem } from '../cpq-list-box/ListBoxItem';
import { ILookup } from '../common/interfaces/ILookup.interface';
import { TenantSettings } from '../common/services/tenant-settings.service';

@Injectable()

export class CpqFormulaBuilderService {
    constructor(private http: HttpClient, public tenantSettings: TenantSettings) { }

    public getLookups(fbConfig: FormulaBuilderConfig): FormulaBuilderLookup {
        const lookups = new FormulaBuilderLookup();
        
        if (fbConfig.formulaBuilderType === FormulaBuilderType.Product) {
            this.getVariables().subscribe(variables => {
                lookups.variables = variables;
            });
            this.getSequenceOperators().subscribe(sequenceOperators => {
                lookups.sequenceOperators = sequenceOperators;
            });

            this.getSequences().subscribe(sequences => {
                lookups.sequences = sequences;
            });

            if (fbConfig.isTrigger) {
                if(fbConfig.ftype == 2) {
                    this.getConditions().subscribe(conditions => {
                        lookups.actions = conditions;
                    });
                }
                else {
                    this.getActions(fbConfig.ftype).subscribe(actions => {
                        lookups.actions = actions;
                    });
                }

                if(fbConfig.productId > 0) {
                    this.getActionAttributeValues(fbConfig.productId).subscribe(actionAttributeValues => {
                        lookups.actionAttributeValues = actionAttributeValues;
                        
                        let counter = 1;
                        actionAttributeValues.forEach(attribute => lookups.actionAttributes.push(new ListBoxItem(counter++, attribute.text)));
                    });
                }
            }
        }
        else if (fbConfig.formulaBuilderType === FormulaBuilderType.Cart) {
            this.getCartActions(fbConfig.ftype).subscribe(cartActions => {
                lookups.cartActions = cartActions;
            });
        }

        this.getOperators().subscribe(operations => {
            lookups.operations = operations;
        });

        return lookups;
    }

    public getUserParameters(): Array<ILookup<number, string>> {
        let result = [
            { id: 1, text: "ID"},
            { id: 2, text: "USERNAME"},
            { id: 3, text: "NAME"},
            { id: 4, text: "TYPE"},
            { id: 5, text: "EMAIL"},
            { id: 6, text: "EXPDATE"},
            { id: 8, text: "LASTLOGINTIME"},
            { id: 9, text: "LASTLOGINIP"},
            { id: 10, text: "LASTLOGINCLIENT"},
            { id: 11, text: "COMPANYID"},
            { id: 12, text: "USER_ID_CODE"},
            { id: 13, text: "USER_ADDR1"},
            { id: 14, text: "USER_ADDR2"},
            { id: 15, text: "USER_ZIP_CODE"},
            { id: 16, text: "USER_CITY"},
            { id: 17, text: "USER_STATE"},
            { id: 19, text: "MANAGING_PARENT"},
            { id: 20, text: "ORDERING_PARENT"},
            { id: 21, text: "REQUESTED_COMPANY"},
            { id: 22, text: "PHONE"},
            { id: 23, text: "FAX"},
            { id: 24, text: "COUNTRY"},
            { id: 25, text: "COUNTRY_ABBREVIATION"},
            { id: 26, text: "TITLE"},
            { id: 27, text: "PERSONALXML"},
            { id: 28, text: "APPROVE_PARENT"},
            { id: 29, text: "TERITORY_ID"},
            { id: 30, text: "MUST_CHANGE_PASSWORD"},
            { id: 31, text: "PASSWORD_LOCKED"},
            { id: 32, text: "TERRITORY_NAME"},
            { id: 33, text: "USER_TYPE_NAME"},
            { id: 34, text: "COMPANY_NAME"},
            { id: 35, text: "APPROVE_PARENT_NAME"},
            { id: 36, text: "ORDERING_PARENT_NAME"},
            { id: 37, text: "MANAGING_PARENT_NAME"},
        ];

        if (!this.tenantSettings.useNewQuoteEngine) {
            result.push({ id: 7, text: "LOCKFLAG"});
            result.push({ id: 18, text: "MULTIPLIER"});
        }
        return result;
    }

    public getCustomerParameters(): Array<ILookup<number, string>> {
        return [
            { id: 1, text: "CUSTOMER_FIRST_NAME"},
            { id: 2, text: "CUSTOMER_LAST_NAME"},
            { id: 3, text: "CUSTOMER_E_MAIL_ADDR"},
            { id: 4, text: "CUSTOMER_TITLE"},
            { id: 5, text: "CUSTOMER_COMPANY"},
            { id: 6, text: "CUSTOMER_ADDR1"},
            { id: 7, text: "CUSTOMER_ADDR2"},
            { id: 8, text: "CUSTOMER_CITY"},
            { id: 9, text: "CUSTOMER_STATE"},
            { id: 10, text: "CUSTOMER_PROVINCE"},
            { id: 11, text: "CUSTOMER_COUNTRY"},
            { id: 12, text: "CUSTOMER_ZIP_CODE"},
            { id: 13, text: "CUSTOMER_BUSINESS_PHONE"},
            { id: 14, text: "CUSTOMER_BUSINESS_FAX"},
            { id: 15, text: "CUSTOMER_PRIMARY_INDUSTRY"},
            { id: 16, text: "CUSTOMER_PASSWORD"},
            { id: 17, text: "SOURCE_ID"},
            { id: 18, text: "USERID"},
            { id: 19, text: "REVISIONNO"},
            { id: 20, text: "CUSTOMER_ID"},
            { id: 21, text: "RECORD_STATUS"},
            { id: 22, text: "TERITORY_ID"},
            { id: 23, text: "ID"},
            { id: 24, text: "ACTIVE"},
            { id: 25, text: "CUSTOMER_CODE"},
            { id: 26, text: "CUSTOMER_TYPE"},
            { id: 27, text: "CREDIT_TERMS"},
            { id: 28, text: "COUNTRY_NAME"},
            { id: 29, text: "TERRITORY_NAME"}
        ]
    }

    public getShipToParameters(): Array<ILookup<number, string>> {
        return [
            { id: 1, text: "SHOP_CART_ID"},
            { id: 2, text: "SHIP_FIRST_NAME"},
            { id: 3, text: "SHIP_LAST_NAME"},
            { id: 4, text: "SHIP_E_MAIL_ADDR"},
            { id: 5, text: "SHIP_TITLE"},
            { id: 6, text: "SHIP_COMPANY"},
            { id: 7, text: "SHIP_ADDR1"},
            { id: 8, text: "SHIP_ADDR2"},
            { id: 9, text: "SHIP_CITY"},
            { id: 10, text: "SHIP_STATE"},
            { id: 11, text: "SHIP_PROVINCE"},
            { id: 12, text: "SHIP_COUNTRY"},
            { id: 13, text: "SHIP_ZIP_CODE"},
            { id: 14, text: "SHIP_BUSINESS_PHONE"},
            { id: 15, text: "SHIP_BUSINESS_FAX"},
            { id: 16, text: "SHIP_PRIMARY_INDUSTRY"},
            { id: 17, text: "SHIP_PASSWORD"},
            { id: 18, text: "SOURCE_ID"},
            { id: 19, text: "CUSTOMER_ID"},
            { id: 20, text: "USERID"},
            { id: 21, text: "REVISIONNO"},
            { id: 22, text: "SHIP_ID"}
        ]
    }

    public getVariables(): Observable<Array<FormulaBuilderExpression>> {
        return this.http.get<Array<FormulaBuilderExpression>>(`${UrlPrefix.OLD_SETUP_API_PREFIX}/formulaBuilder/variables`);
    }

    public getSequenceOperators(): Observable<Array<FormulaBuilderExpression>> {
        return this.http.get<Array<FormulaBuilderExpression>>(`${UrlPrefix.OLD_SETUP_API_PREFIX}/formulaBuilder/sequenceOperators`);
    }

    public getOperators(): Observable<Array<FormulaBuilderExpression>> {
        return this.http.get<Array<FormulaBuilderExpression>>(`${UrlPrefix.OLD_SETUP_API_PREFIX}/formulaBuilder/operators`);
    }

    public getActions(fType: number): Observable<Array<FormulaBuilderExpression>> {
        return this.http.get<Array<FormulaBuilderExpression>>(`${UrlPrefix.OLD_SETUP_API_PREFIX}/formulaBuilder/actions?fType=${fType}`);
    }

    public getCartActions(fType: number): Observable<Array<FormulaBuilderExpression>> {
        return this.http.get<Array<FormulaBuilderExpression>>(`${UrlPrefix.OLD_SETUP_API_PREFIX}/formulaBuilder/cartActions?fType=${fType}`);
    }

    public getConditions(): Observable<Array<FormulaBuilderExpression>> {
        return this.http.get<Array<FormulaBuilderExpression>>(`${UrlPrefix.OLD_SETUP_API_PREFIX}/formulaBuilder/conditions`);
    }

    public getAttributes(productId: number): Observable<Array<ILookup<number, string>>> {
        return this.http.get<Array<ILookup<number, string>>>(`${UrlPrefix.OLD_SETUP_API_PREFIX}/formulaBuilder/productAttributes?productId=${productId}`);
    }

    public getAttributeValues(productId: number, attributeId: number): Observable<Array<AttributeValue>> {
        return this.http.get<Array<AttributeValue>>(
            `${UrlPrefix.OLD_SETUP_API_PREFIX}/formulaBuilder/productAttributeValues?productId=${productId}&productAttributeId=${attributeId}`);
    }

    public getSequences(): Observable<Array<Sequence>> {
        return this.http.get<Array<Sequence>>(`${UrlPrefix.OLD_SETUP_API_PREFIX}/formulaBuilder/sequences`);
    }

    public getActionAttributeValues(productId: number): Observable<Array<ActionAttribute>> {
        return this.http.get<Array<ActionAttribute>>(`${UrlPrefix.OLD_SETUP_API_PREFIX}/formulaBuilder/actionAttributeValues?productId=${productId}`);
    }

    public checkSyntax(syntaxCheck: SyntaxCheck): Observable<SyntaxCheckResponse> {
        return this.http.post<SyntaxCheckResponse>(`${UrlPrefix.OLD_SETUP_API_PREFIX}/formulaBuilder/checkSyntax`, syntaxCheck);
    }

    public checkCartTagSyntax(syntaxCheck: SyntaxCheck): Observable<SyntaxCheckResponse> {
        return this.http.post<SyntaxCheckResponse>(`${UrlPrefix.OLD_SETUP_API_PREFIX}/formulaBuilder/checkCartTagSyntax`, syntaxCheck);
    }

    public getVisualizeResponse(conditionFormula: string): Observable<SyntaxVisualizeResponse> {
        return this.http.get<SyntaxVisualizeResponse>(`${UrlPrefix.OLD_SETUP_API_PREFIX}/formulaBuilder/visualize?conditionFormula=${conditionFormula}`);
    }

    public getCustomFields(): Observable<Array<ILookup<number, string>>> {
        return this.http.get<Array<ILookup<number, string>>>(`${UrlPrefix.OLD_SETUP_API_PREFIX}/lookups/customFields`);
    }
}
